import React from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { AlertOctagon, Loader2, X, AlertCircle, Send, Paperclip, CheckCircle2 } from "lucide-react";

const REASON_LABELS = {
  damage: "Property damage",
  no_show: "Driver / customer no-show",
  late: "Significant delay",
  payment: "Payment issue",
  safety: "Safety / behaviour",
  other: "Other",
};

const STATUS_META = {
  open: { label: "Open", cls: "bg-[#F5A623]/10 text-[#A66800] border-[#F5A623]/40" },
  admin_reviewing: { label: "Admin reviewing", cls: "bg-[#1E4D55]/10 text-[#1E4D55] border-[#1E4D55]/30" },
  resolved: { label: "Resolved", cls: "bg-[#5BB247]/10 text-[#2c8642] border-[#5BB247]/40" },
};

export default function DisputesSection({ role }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [openCreate, setOpenCreate] = React.useState(false);
  const [openThread, setOpenThread] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/disputes");
      setItems(data.disputes || []);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load disputes");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  return (
    <section className="mb-12" data-testid={`disputes-section-${role}`}>
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A]">Disputes</h2>
          <p className="mt-1 text-sm text-[#475569]">If something went wrong on a job, open a dispute and our team will mediate.</p>
        </div>
        {role !== "admin" && (
          <button
            onClick={() => setOpenCreate(true)}
            data-testid="open-dispute-button"
            className="inline-flex items-center gap-2 bg-white border border-[#1E4D55] text-[#1E4D55] hover:bg-[#1E4D55]/5 px-4 py-2.5 text-sm font-semibold transition-colors"
          >
            <AlertOctagon size={14} /> Open dispute
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="disputes-error">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-[#475569] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-[#0A0A0A]/20 p-8 text-center" data-testid="disputes-empty">
          <CheckCircle2 size={28} className="mx-auto text-[#5BB247]" />
          <p className="mt-3 text-sm font-semibold text-[#0A0A0A]">No disputes — everything&apos;s running smoothly.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="disputes-list">
          {items.map((d) => {
            const meta = STATUS_META[d.status] || STATUS_META.open;
            return (
              <button
                key={d.id}
                onClick={() => setOpenThread(d.id)}
                data-testid={`dispute-row-${d.id}`}
                className="w-full bg-white border border-[#0A0A0A]/10 hover:border-[#1E4D55] p-4 text-left transition-colors"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 border ${meta.cls}`}>
                    {meta.label}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#475569]">{REASON_LABELS[d.reason] || d.reason}</span>
                  <span className="text-xs text-[#475569]">· Job {String(d.job_id).slice(-6).toUpperCase()}</span>
                  <span className="ml-auto text-[11px] text-[#475569]">{new Date(d.created_at).toLocaleDateString("en-GB")}</span>
                </div>
                <p className="mt-2 text-sm text-[#0A0A0A] line-clamp-2">{d.summary}</p>
              </button>
            );
          })}
        </div>
      )}

      {openCreate && <CreateDisputeModal onClose={() => setOpenCreate(false)} onCreated={async () => { setOpenCreate(false); await load(); }} />}
      {openThread && <DisputeThreadModal disputeId={openThread} role={role} onClose={() => setOpenThread(null)} onChanged={load} />}
    </section>
  );
}

function CreateDisputeModal({ onClose, onCreated }) {
  const [jobId, setJobId] = React.useState("");
  const [reason, setReason] = React.useState("damage");
  const [summary, setSummary] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await apiClient.post("/disputes", { job_id: jobId.trim(), reason, summary: summary.trim() });
      onCreated();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to open dispute");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="create-dispute-modal">
      <div className="bg-white border border-[#0A0A0A]/10 max-w-lg w-full p-8 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">New dispute</p>
            <h3 className="font-display mt-2 text-2xl font-bold tracking-tight text-[#0A0A0A]">Tell us what went wrong</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F5F5F5]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">Job ID</label>
            <input
              required
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              placeholder="68f1...d3e7"
              data-testid="create-dispute-job-id"
              className="w-full px-4 py-3 border border-[#0A0A0A]/15 text-sm font-mono"
            />
            <p className="mt-1 text-[11px] text-[#475569]">Find this in the URL bar when viewing a job.</p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">Reason</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="create-dispute-reason"
              className="w-full px-4 py-3 border border-[#0A0A0A]/15 text-sm bg-white"
            >
              {Object.entries(REASON_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">What happened?</label>
            <textarea
              required
              minLength={10}
              maxLength={2000}
              rows={5}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Describe what happened in detail. Include times, locations, names, anything that helps."
              data-testid="create-dispute-summary"
              className="w-full px-4 py-3 border border-[#0A0A0A]/15 text-sm"
            />
          </div>
          {error && (
            <div className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="create-dispute-error">
              <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            data-testid="create-dispute-submit"
            className="w-full bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3 font-semibold transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {submitting ? "Opening…" : "Open dispute"}
          </button>
        </form>
      </div>
    </div>
  );
}

function DisputeThreadModal({ disputeId, role, onClose, onChanged }) {
  const [dispute, setDispute] = React.useState(null);
  const [body, setBody] = React.useState("");
  const [posting, setPosting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [resolving, setResolving] = React.useState(false);
  const [resType, setResType] = React.useState("refund_full");
  const [refundAmount, setRefundAmount] = React.useState("");
  const [resNotes, setResNotes] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/disputes/${disputeId}`);
      setDispute(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load dispute");
    }
  }, [disputeId]);

  React.useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setPosting(true);
    try {
      await apiClient.post(`/disputes/${disputeId}/messages`, { body: body.trim() });
      setBody("");
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send");
    } finally {
      setPosting(false);
    }
  };

  const resolve = async () => {
    setResolving(true);
    try {
      const payload = {
        resolution_type: resType,
        notes: resNotes || null,
      };
      if (resType === "refund_partial") payload.refund_amount_pence = Math.round(parseFloat(refundAmount || "0") * 100);
      await apiClient.post(`/admin/disputes/${disputeId}/resolve`, payload);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to resolve");
    } finally {
      setResolving(false);
    }
  };

  if (!dispute) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center" onClick={onClose}>
        <Loader2 size={22} className="animate-spin text-white" />
      </div>
    );
  }

  const meta = STATUS_META[dispute.status] || STATUS_META.open;
  const isResolved = dispute.status === "resolved";

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="dispute-thread-modal">
      <div className="bg-white border border-[#0A0A0A]/10 max-w-2xl w-full p-8 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">Dispute · Job {String(dispute.job_id).slice(-6).toUpperCase()}</p>
            <h3 className="font-display mt-2 text-2xl font-bold tracking-tight text-[#0A0A0A]">{REASON_LABELS[dispute.reason] || dispute.reason}</h3>
            <span className={`mt-2 inline-flex text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 border ${meta.cls}`}>{meta.label}</span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F5F5F5]"><X size={18} /></button>
        </div>

        <div className="mt-6 bg-[#F5F5F5] border border-[#0A0A0A]/10 p-4">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#475569]">Original report — by {dispute.opener_role}</p>
          <p className="mt-2 text-sm text-[#0A0A0A] whitespace-pre-wrap">{dispute.summary}</p>
        </div>

        <div className="mt-6 space-y-3 max-h-[300px] overflow-y-auto" data-testid="dispute-messages">
          {(dispute.messages || []).map((m, i) => (
            <div key={i} className={`p-3 border ${m.sender_role === 'admin' ? 'bg-[#1E4D55]/5 border-[#1E4D55]/30' : 'bg-white border-[#0A0A0A]/10'}`} data-testid={`dispute-msg-${i}`}>
              <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569]">
                {m.sender_role} · {new Date(m.created_at).toLocaleString("en-GB")}
              </p>
              <p className="mt-1 text-sm text-[#0A0A0A] whitespace-pre-wrap">{m.body}</p>
            </div>
          ))}
        </div>

        {!isResolved && (
          <form onSubmit={send} className="mt-4 flex items-start gap-2">
            <textarea
              rows={2}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Add a reply…"
              data-testid="dispute-reply-input"
              className="flex-1 px-3 py-2 border border-[#0A0A0A]/15 text-sm"
            />
            <button
              type="submit"
              disabled={posting || !body.trim()}
              data-testid="dispute-reply-send"
              className="shrink-0 inline-flex items-center gap-1.5 bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {posting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Send
            </button>
          </form>
        )}

        {role === "admin" && !isResolved && (
          <div className="mt-6 p-4 bg-[#F5A623]/5 border border-[#F5A623]/40" data-testid="dispute-resolve-panel">
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#A66800]">Admin resolution</p>
            <div className="mt-3 space-y-2">
              <select
                value={resType}
                onChange={(e) => setResType(e.target.value)}
                data-testid="dispute-resolve-type"
                className="w-full px-3 py-2 border border-[#0A0A0A]/15 text-sm bg-white"
              >
                <option value="refund_full">Refund customer in full</option>
                <option value="refund_partial">Partial refund (specify amount)</option>
                <option value="release_to_driver">Release escrow to driver</option>
                <option value="no_action">No action — close ticket</option>
              </select>
              {resType === "refund_partial" && (
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder="Refund amount in £ (e.g. 25.00)"
                  data-testid="dispute-resolve-amount"
                  className="w-full px-3 py-2 border border-[#0A0A0A]/15 text-sm"
                />
              )}
              <textarea
                rows={2}
                value={resNotes}
                onChange={(e) => setResNotes(e.target.value)}
                placeholder="Internal notes (visible to both parties)"
                data-testid="dispute-resolve-notes"
                className="w-full px-3 py-2 border border-[#0A0A0A]/15 text-sm"
              />
              <button
                onClick={resolve}
                disabled={resolving}
                data-testid="dispute-resolve-submit"
                className="w-full bg-[#B91C1C] hover:bg-[#991B1B] text-white px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {resolving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Resolve dispute
              </button>
            </div>
          </div>
        )}

        {isResolved && (
          <div className="mt-6 p-4 bg-[#5BB247]/5 border border-[#5BB247]/40" data-testid="dispute-resolved-banner">
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#2c8642]">Resolved · {dispute.resolution_type}</p>
            {dispute.resolution_notes && <p className="mt-1 text-sm text-[#0A0A0A]">{dispute.resolution_notes}</p>}
            {role === "admin" && dispute.resolution_stripe_action && dispute.resolution_stripe_action.ok === false && dispute.resolution_stripe_action.error && (
              <p data-testid="dispute-stripe-action-error" className="mt-2 text-xs text-[#B91C1C]">
                ⚠ Stripe refund failed: {dispute.resolution_stripe_action.error}. Issue manually from Stripe Dashboard.
              </p>
            )}
          </div>
        )}

        {error && (
          <div className="mt-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="dispute-thread-error">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
}
